import React from 'react';
import { Droplet, Gauge, Ruler } from 'lucide-react';

/**
 * Компонент дополнительных параметров расчета в зависимости от типа
 */
const AdditionalParametersSection = ({ formData, handleInputChange, errors, calculationType }) => {
  return (
    <div className="mb-6 bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
      <h3 className="text-base font-medium text-gray-900 dark:text-white mb-4">
        {calculationType === 'flowRate' ? 'Параметры для расчета расхода' :
         calculationType === 'pressure' ? 'Параметры для расчета перепада давления' :
         'Параметры для подбора диаметра'}
      </h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Параметры для расчета расхода */}
        {formData.calculationType === 'flowRate' && (
          <div>
            <label htmlFor="pressureDrop" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Gauge className="w-4 h-4 mr-2 text-blue-500" />
              Перепад давления (бар)
            </label>
            <input
              type="text"
              id="pressureDrop"
              name="pressureDrop"
              value={formData.pressureDrop}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.pressureDrop ? 'input-error' : ''}`}
              placeholder="Введите перепад давления"
            />
            {errors.pressureDrop && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.pressureDrop}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Разность давлений между началом и концом трубы. Для типичных водопроводных систем: 0.2-1 бар на 100 м.
            </p>
          </div>
        )}
        
        {/* Параметры для расчета перепада давления */}
        {formData.calculationType === 'pressure' && (
          <div>
            <label htmlFor="flowRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
              <Droplet className="w-4 h-4 mr-2 text-blue-500" />
              Расход воды (м³/ч)
            </label>
            <input
              type="text"
              id="flowRate"
              name="flowRate"
              value={formData.flowRate}
              onChange={handleInputChange}
              className={`form-input w-full ${errors.flowRate ? 'input-error' : ''}`}
              placeholder="Введите расход воды"
            />
            {errors.flowRate && (
              <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                {errors.flowRate}
              </p>
            )}
            <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
              Объем воды, проходящий через трубу за единицу времени. Для бытовых систем обычно 0.5-5 м³/ч.
            </p>
          </div>
        )}
        
        {/* Параметры для расчета диаметра */}
        {formData.calculationType === 'diameter' && (
          <>
            <div>
              <label htmlFor="requiredFlowRate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Droplet className="w-4 h-4 mr-2 text-blue-500" />
                Требуемый расход (м³/ч)
              </label>
              <input
                type="text"
                id="requiredFlowRate"
                name="requiredFlowRate"
                value={formData.requiredFlowRate}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.requiredFlowRate ? 'input-error' : ''}`}
                placeholder="Введите требуемый расход"
              />
              {errors.requiredFlowRate && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.requiredFlowRate}
                </p>
              )}
            </div>
            
            <div>
              <label htmlFor="maxPressureDrop" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center">
                <Gauge className="w-4 h-4 mr-2 text-blue-500" />
                Макс. перепад давления (бар)
              </label>
              <input
                type="text"
                id="maxPressureDrop"
                name="maxPressureDrop"
                value={formData.maxPressureDrop}
                onChange={handleInputChange}
                className={`form-input w-full ${errors.maxPressureDrop ? 'input-error' : ''}`}
                placeholder="Максимально допустимый перепад"
              />
              {errors.maxPressureDrop && (
                <p className="mt-1 text-xs text-red-500 dark:text-red-400">
                  {errors.maxPressureDrop}
                </p>
              )}
            </div>
            
            <div className="col-span-full">
              <p className="text-xs text-gray-600 dark:text-gray-400">
                Калькулятор подберет минимальный диаметр трубы, при котором расход воды не превысит требуемый,
                а перепад давления не будет больше указанного максимального значения.
              </p>
            </div>
          </>
        )}
      </div>
      
      {/* Рекомендации по параметрам */}
      <div className="mt-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-blue-100 dark:border-blue-900">
        <h4 className="text-sm font-medium text-blue-800 dark:text-blue-300 mb-1">
          Рекомендации по выбору параметров:
        </h4>
        <ul className="text-xs text-blue-700 dark:text-blue-400 list-disc pl-4 space-y-1">
          <li>Для бытовых систем оптимальная скорость потока: 1-2 м/с</li>
          <li>Для систем пожаротушения: 2-3 м/с</li>
          <li>Для промышленных систем: до 5 м/с</li>
          <li>При скорости ниже 0.5 м/с возможно образование отложений</li>
          <li>При скорости выше 3 м/с увеличивается шум и износ труб</li>
        </ul>
      </div>
    </div>
  );
};

export default AdditionalParametersSection;